import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { NotFoundPage } from './NotFoundPage';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, ExternalLinkIcon, InfoIcon, LockIcon, TargetIcon } from 'lucide-react';
import { getDisplayDomain } from '../utils/url';
import { clientCases, getClientCaseBySlug } from '../data/allClientCases';
import { buildBreadcrumbList, buildCaseStudySchema } from '../lib/structuredData';
export const ClientCasePage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const clientCase = getClientCaseBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Onbekende case-slug: toon de echte 404 in plaats van stil door te sturen.
  if (!clientCase) {
    return <NotFoundPage data-id="element-1295" data-mp="3j59gcj7hdec5" />;
  }
  const relatedCases = clientCases.filter(c => c.slug !== clientCase.slug).slice(0, 3);
  const renderContentBlock = (block: any, index: number) => {
    switch (block.type) {
      case 'heading':
        return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#111827] mt-12 mb-6" data-id="element-1296" data-mp="toii2272kxfw">
            {block.content}
          </h2>;
      case 'heading3':
        return <h3 key={index} className="text-xl md:text-2xl font-bold text-[#111827] mt-8 mb-4" data-id="element-1297" data-mp="13leqk7wyxjkg">
            {block.content}
          </h3>;
      case 'paragraph':
        return <p key={index} className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-1298" data-mp="1cks2r8ch8m8v">
            {block.content}
          </p>;
      case 'list':
        return <ul key={index} className="space-y-3 mb-8 ml-6 list-disc text-lg text-[#4B5563]" data-id="element-1299" data-mp="1hcs9bjn1ydef">
            {block.items?.map((item: string, i: number) => <li key={i} className="pl-2 leading-relaxed" data-id="element-1300" data-mp="1rag4gaoklmx4">
                {item}
              </li>)}
          </ul>;
      case 'callout':
        return <div key={index} className="bg-[#F7F8FA] border-l-4 border-[#007AFF] p-6 rounded-r-lg my-8" data-id="element-1301" data-mp="1wa2fqeku8t3a">
            <div className="flex items-start gap-4" data-id="element-1302" data-mp="16lnalulkh7kc">
              <InfoIcon className="w-6 h-6 text-[#007AFF] flex-shrink-0 mt-1" data-id="element-1303" data-mp="11mamz7mvfwgp" />
              <p className="text-lg text-[#1B2B44] font-medium leading-relaxed m-0" data-id="element-1304" data-mp="11k7sobeorh66">
                {block.content}
              </p>
            </div>
          </div>;
      case 'formula':
        return <div key={index} className="bg-[#1B2B44] text-white p-8 rounded-xl my-8 text-center shadow-lg" data-id="element-1305" data-mp="1bidoqcdt9t34">
            <span className="text-sm text-white/60 uppercase tracking-wider font-semibold mb-3 block" data-id="element-1306" data-mp="1kck3lmjenqrx">
              De Formule
            </span>
            <p className="text-xl md:text-2xl font-bold leading-relaxed" data-id="element-1307" data-mp="1z8huapak82wr">
              {block.content}
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full min-h-screen bg-white font-sans" data-id="element-1308" data-mp="4v04evmomtyk">
      <SEO title={`${clientCase.clientName} | Klantcase Noord Digital`} description={clientCase.metaDescription} canonical={`https://noorddigital.nl/cases/${clientCase.slug}`} keywords={`${clientCase.clientName}, ${clientCase.sector}, klantcase, website laten maken ${clientCase.sector.toLowerCase()}, Noord Digital`} ogType="article" ogImage={clientCase.heroImage} jsonLd={[buildCaseStudySchema({
      clientName: clientCase.clientName,
      description: clientCase.metaDescription,
      path: `/cases/${clientCase.slug}`,
      sector: clientCase.sector,
      image: clientCase.heroImage,
      clientWebsite: clientCase.website
    }), buildBreadcrumbList([{
      name: 'Cases',
      path: '/#client-cases'
    }, {
      name: clientCase.clientName,
      path: `/cases/${clientCase.slug}`
    }])]} data-id="element-1309" data-mp="4xmk5ijq8frv" />
      <Header data-id="element-1310" data-mp="1nu7rj61ba2v6" />

      {/* Breadcrumb & Hero */}
      <div className="bg-[#F7F8FA] pt-24 pb-16 border-b border-gray-200" data-id="element-1311" data-mp="1nuzoak6na6n1">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1312" data-mp="3d94hz2hrgw5z">
          <nav className="flex items-center text-sm text-[#6B7280] mb-8" data-id="element-1313" data-mp="3i88sbwgf8zxw">
            <Link to="/" className="hover:text-[#007AFF] transition-colors" data-id="element-1314" data-mp="3i64m37v1voug">
              Home
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1315" data-mp="1d7ed1uzdnnjn" />
            <Link to="/bibliotheek" className="hover:text-[#007AFF] transition-colors" data-id="element-1316" data-mp="1d7o7pvvdaivo">
              Bibliotheek
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1317" data-mp="146gjy7qh90fu" />
            <span className="text-[#111827] font-medium truncate" data-id="element-1318" data-mp="145ydyje0j7uq">
              {clientCase.clientName}
            </span>
          </nav>

          <div className="max-w-3xl" data-id="element-1319" data-mp="ugn8d6qc4lbl">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6" data-id="element-1320" data-mp="uflwj01hvust">
              <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-semibold rounded-full" data-id="element-1321" data-mp="ph27urvio4wr">
                {clientCase.sector}
              </span>
              {clientCase.website ? <a href={clientCase.website} target="_blank" rel="noopener noreferrer" aria-label={`Bekijk de website van ${clientCase.clientName} (opent in nieuw tabblad)`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4B5563] hover:text-[#004AC7] transition-colors duration-200" data-id="element-1322" data-mp="ghf5o3uyqv2n">
                  {getDisplayDomain(clientCase.website)}
                  <ExternalLinkIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1323" data-mp="2s6bw4y9ryg41" />
                </a> : clientCase.isPrivate ? <span className="inline-flex items-center gap-1.5 text-sm text-[#6B7280]" data-id="element-1324" data-mp="2x1hj80ewy0tp">
                  <LockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1325" data-mp="312t6hzabe9yq" />
                  Interne applicatie, niet openbaar toegankelijk
                </span> : null}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] leading-tight mb-6" data-id="element-1326" data-mp="29dzllo8yw8it">
              {clientCase.clientName}
            </h1>
            <p className="text-xl text-[#6B7280] leading-relaxed" data-id="element-1327" data-mp="2i6l1mwl7s13y">
              {clientCase.shortDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content & Sidebar */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] py-16" data-id="element-1328" data-mp="1pp8rc0y2yu3h">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1329" data-mp="2t0efvt3xi3ay">
          {/* Article Content */}
          <div className="lg:col-span-8" data-id="element-1330" data-mp="2o1uohd6kfzb7">
            <div className="mb-12 rounded-2xl overflow-hidden shadow-lg" data-id="element-1331" data-mp="2o11sn795au7u">
              <img src={clientCase.heroImage} alt={`${clientCase.clientName} case study`} className={clientCase.heroImageContain ? 'w-full h-72 md:h-96 object-contain bg-[#EEF5FA] p-8' : 'w-full h-auto object-cover object-top max-h-[500px]'} data-id="element-1332" data-mp="32qqwxbcjbecc" />
            </div>
            <article className="prose prose-lg max-w-none prose-headings:text-[#111827] prose-p:text-[#4B5563] prose-a:text-[#007AFF]" data-id="element-1333" data-mp="2a3mvthgkbk5u">
              {clientCase.content.map((block, index) => renderContentBlock(block, index))}
            </article>

            {/* Link naar de live website van de klant */}
            {clientCase.website ? <a href={clientCase.website} target="_blank" rel="noopener noreferrer" className="mt-10 inline-flex items-center gap-2 bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:bg-[#005FCC] transition-colors duration-200" data-id="element-1334" data-mp="3ugvp3w6jd5o4">
                Bekijk de website van {clientCase.clientName}
                <ExternalLinkIcon className="w-4 h-4" aria-hidden="true" data-id="element-1335" data-mp="6ubakrynhigd" />
              </a> : clientCase.isPrivate ? <p className="mt-10 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-[#F7F8FA] py-3 px-5 text-sm text-[#6B7280]" data-id="element-1336" data-mp="3o2oqi9h3l2jb">
                <LockIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" data-id="element-1337" data-mp="kkofelg8wh1h" />
                Interne applicatie, niet openbaar toegankelijk
              </p> : null}

            {/* Author / Bio block */}
            <div className="mt-16 pt-8 border-t border-gray-200 flex items-center gap-6" data-id="element-1338" data-mp="auv7bufbak1z">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full bg-[#F7F8FA] border border-gray-200" loading="lazy" data-id="element-1339" data-mp="ftya187tke6s" />
              <div data-id="element-1340" data-mp="189zabmawzh7p">
                <h4 className="font-bold text-[#111827]" data-id="element-1341" data-mp="18aiu78k4kfod">Norden</h4>
                <p className="text-[#6B7280] text-sm" data-id="element-1342" data-mp="34ddsirqw88oh">
                  Klantcases bij Noord Digital
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-1343" data-mp="2vl3gr60cnv54">
            <div className="sticky top-24 space-y-8" data-id="element-1344" data-mp="2vlvaebadzisr">
              {/* Back to Bibliotheek */}
              <Link to="/bibliotheek" className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-[#F7F8FA] hover:bg-gray-100 text-[#1B2B44] font-semibold rounded-xl transition-colors border border-gray-200" data-id="element-1345" data-mp="2qna908jeet2q">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-1346" data-mp="3jbfu4wed6k33" />
                Terug naar Bibliotheek
              </Link>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#007AFF] to-[#1B2B44] rounded-2xl p-8 text-white shadow-xl" data-id="element-1347" data-mp="39cqkts0ja7yg">
                <h3 className="text-xl font-bold mb-3" data-id="element-1348" data-mp="22wobvcaxmt7i">
                  Ook zulke resultaten behalen?
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6" data-id="element-1349" data-mp="1xvqkc9418mtc">
                  Ontdek wat we voor jouw bedrijf kunnen betekenen. Vraag een
                  gratis scan aan en we laten je direct zien waar je kansen
                  liggen.
                </p>
                <a href="/prijsscan" className="block w-full py-3 px-4 bg-white text-[#007AFF] text-center font-bold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1350" data-mp="1xraqj9ft3zxf">
                  Start de SEO scan
                </a>
              </div>

              {/* Related Cases */}
              {relatedCases.length > 0 && <div className="bg-white border border-gray-200 rounded-2xl p-6" data-id="element-1351" data-mp="2bhxsrbks07y9">
                  <h3 className="text-lg font-bold text-[#111827] mb-4 flex items-center gap-2" data-id="element-1352" data-mp="2ghitnrjiaqdm">
                    <TargetIcon className="w-5 h-5 text-[#007AFF]" data-id="element-1353" data-mp="2lholp8touew8" />
                    Andere cases
                  </h3>
                  <ul className="space-y-3" data-id="element-1354" data-mp="m5cg4m353xi5">
                    {relatedCases.map((relCase, idx) => <li key={idx} data-id="element-1355" data-mp="r2cba1ygbfuh" data-mpi={idx}>
                        <Link to={`/cases/${relCase.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug" data-id="element-1356" data-mp="r3dz770kfwtm">
                          <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" data-id="element-1357" data-mp="boojlqs3ryya" />
                          {relCase.clientName}
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* More Cases Grid */}
      {relatedCases.length > 0 && <div className="bg-[#F7F8FA] py-20 border-t border-gray-200" data-id="element-1358" data-mp="umiyumjtr6p4">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1359" data-mp="ukoh1h3unup9">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-10 text-center" data-id="element-1360" data-mp="zdrazqcjh65z">
              Meer succesverhalen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1361" data-mp="18dxmj6oofw2i">
              {relatedCases.map((relCase, idx) => <Link key={idx} to={`/cases/${relCase.slug}`} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#007AFF]/30 transition-all group" data-id="element-1362" data-mp="1dcqyguobff0k" data-mpi={idx}>
                  <div className="h-40 mb-4 overflow-hidden rounded-lg" data-id="element-1363" data-mp="1neky700u98aj">
                    <img src={relCase.heroImage} alt={relCase.clientName} className={`w-full h-full group-hover:scale-105 transition-transform duration-500 ${relCase.heroImageContain ? 'object-contain bg-[#EEF5FA] p-3' : 'object-cover'}`} loading="lazy" data-id="element-1364" data-mp="1igjdybrof2xh" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1B2B44] mb-3 group-hover:text-[#007AFF] transition-colors" data-id="element-1365" data-mp="2663y2xzkg68c">
                    {relCase.clientName}
                  </h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed mb-4 line-clamp-3" data-id="element-1366" data-mp="1wgu41cl5wdhl">
                    {relCase.shortDescription}
                  </p>
                  <span className="text-[#007AFF] text-sm font-semibold flex items-center gap-1" data-id="element-1367" data-mp="1se7h9q3clir9">
                    Lees de case <ArrowRightIcon className="w-4 h-4" data-id="element-1368" data-mp="2k7gch4z2vzs4" />
                  </span>
                </Link>)}
            </div>
          </div>
        </div>}

      <Footer data-id="element-1369" data-mp="ia65svao7m53" />
    </div>;
};